import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type {
	AnalyticsMarketplaceStats,
	AnalyticsOrderStats,
	AnalyticsUserStats,
} from "@expertloop/shared";

/** Une valeur et son intitulé. Rien n'est calculé ici. */
function Tile({ label, value }: { label: string; value: string }) {
	return (
		<div className="admin-overview__tile">
			<span className="admin-overview__tile-label">{label}</span>
			<strong className="admin-overview__tile-value">{value}</strong>
		</div>
	);
}

export function PopulationTiles({ users, orders }: { users: AnalyticsUserStats; orders: AnalyticsOrderStats }) {
	const { t } = useI18n();

	return (
		<div className="admin-overview__tiles">
			<Tile label={t("adminOverview.experts")} value={String(users.totalExperts)} />
			<Tile label={t("adminOverview.clients")} value={String(users.totalClients)} />
			<Tile label={t("adminOverview.newUsers")} value={String(users.newUsers)} />
			<Tile label={t("adminOverview.ordersCreated")} value={String(orders.createdInPeriod)} />
			<Tile label={t("adminOverview.capturedOrders")} value={String(orders.capturedInPeriod)} />
		</div>
	);
}

/**
 * Chiffres financiers d'une devise.
 *
 * Une devise a son propre bloc. Le taux de commission n'est affiché que
 * lorsque le serveur en a calculé un : quand rien n'a été encaissé, il est
 * absent, et écrire zéro laisserait croire que la plateforme n'a rien prélevé.
 */
export function MarketplaceTiles({ marketplace }: { marketplace: AnalyticsMarketplaceStats }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	if (marketplace.byCurrency.length === 0) {
		return <p className="admin-overview__state">{t("adminOverview.empty")}</p>;
	}

	return (
		<div className="admin-overview__currencies">
			{marketplace.byCurrency.map((row) => {
				const refunds = marketplace.refundsByCurrency.find((entry) => entry.currency === row.currency);
				const authorized = marketplace.authorizedByCurrency.find((entry) => entry.currency === row.currency);
				const missing = row.ordersMissingPlatformFeeAmount;

				return (
					<div key={row.currency} className="admin-overview__currency">
						<h4 className="admin-overview__currency-title">{row.currency.toUpperCase()}</h4>
						<div className="admin-overview__tiles">
							<Tile label={t("adminOverview.gmv")} value={formatCents(row.gmv, row.currency)} />
							<Tile label={t("adminOverview.platformFee")} value={formatCents(row.platformFee, row.currency)} />
							<Tile label={t("adminOverview.stripeFee")} value={formatCents(row.stripeFee, row.currency)} />
							{row.effectiveFeeRate !== null && (
								<Tile
									label={t("adminOverview.effectiveFeeRate")}
									value={`${(row.effectiveFeeRate * 100).toFixed(2)} %`}
								/>
							)}
							{authorized && (
								<Tile
									label={t("adminOverview.authorized")}
									value={formatCents(authorized.authorizedAmount, row.currency)}
								/>
							)}
							{refunds && (
								<Tile
									label={t("adminOverview.refunded")}
									value={formatCents(refunds.refundedAmount, row.currency)}
								/>
							)}
						</div>
						{missing > 0 && (
							<p className="admin-overview__caveat">
								{t("adminOverview.partialData", {
									count: missing,
									total: row.orderCount,
									plural: missing !== 1 ? "s" : "",
								})}
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
}
